import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ImageSwiperModalComponent } from '../modals/image-swiper-modal/image-swiper-modal.component';
import { SwiperModalComponent } from '../modals/swiper-modal/swiper-modal.component';
import { LocalStorageKey } from '../../core/constants/local-storage-keys';
import { IntroModalType } from '../enums/intro-modal-type.enum';
import {
  SwiperModalStep,
  ImageSwiperModalStep,
  IntroFinished,
} from '../interfaces';
import { Auth0Service } from '@geneplanet/ngx-auth0';
import { delay, first, pluck } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { extractAccount } from '../operators/extract-account';
import { TagsApiService } from './tags-api.service';
import { IntroId } from '../enums/intro-id.enum';

@Injectable({
  providedIn: 'root',
})
@UntilDestroy()
export class IntroModalService {
  private customerId$ = this.auth0.tokens.current$.pipe(
    first(),
    untilDestroyed(this),
    extractAccount(),
    pluck('http://schemas.nomnio.com/2017/5/customer'),
    delay(0)
  );

  constructor(
    private modals: NgbModal,
    private auth0: Auth0Service,
    private tagsApiService: TagsApiService
  ) {}

  checkAndOpenIntroModal(
    introId: IntroId,
    type: IntroModalType,
    steps?: SwiperModalStep[] | ImageSwiperModalStep[],
    confirmButtonText?: string
  ) {
    if (type === IntroModalType.SWIPER) {
      this.checkSwiperModal(
        introId,
        steps as SwiperModalStep[],
        confirmButtonText
      );
    } else if (type === IntroModalType.IMAGE_SWIPER) {
      this.checkImageSwiperModal(introId, steps as ImageSwiperModalStep[]);
    }
  }

  openSwiperModal(
    introId: IntroId,
    steps: SwiperModalStep[],
    confirmButtonText: string,
    confirmed = true,
    containerClass = undefined
  ) {
    const ref = this.modals.open(SwiperModalComponent, {
      backdrop: confirmed ? true : 'static',
      windowClass: 'swiper-modal',
      scrollable: true,
    });
    ref.componentInstance.introId = introId;
    ref.componentInstance.steps = steps;
    ref.componentInstance.confirmButtonText = confirmButtonText;
    ref.componentInstance.confirmed = confirmed;
    if (containerClass) {
      ref.componentInstance.containerClass = containerClass;
    }
  }

  openImageSwiperModal(steps: ImageSwiperModalStep[]) {
    const ref = this.modals.open(ImageSwiperModalComponent, {
      windowClass: 'swiper-modal fullscreen-image',
      scrollable: true,
    });
    ref.componentInstance.steps = steps;
  }

  setIntroModalCompleted(
    introId: IntroId,
    type: IntroModalType,
    customerId?: string
  ) {
    if (type === IntroModalType.SWIPER) {
      this.setSwiperModalCompleted(introId);
    } else if (type === IntroModalType.IMAGE_SWIPER) {
      this.setImageSwiperModalCompleted(introId, customerId);
    }
  }

  private checkSwiperModal(
    introId: IntroId,
    steps: SwiperModalStep[],
    confirmButtonText?: string
  ) {
    this.tagsApiService
      .getIntroFinished(introId)
      ?.subscribe((introFinished: IntroFinished) => {
        if (introFinished?.value === 'false') {
          this.openSwiperModal(
            introId,
            steps as SwiperModalStep[],
            confirmButtonText,
            false
          );
        }
      });
  }

  private checkImageSwiperModal(
    introId: IntroId,
    steps: ImageSwiperModalStep[]
  ) {
    const introIds = this.getIntroCompletedLocalStorage();
    this.customerId$.subscribe((customerId) => {
      // If user has old localstorage array instead ob object, then delete it to make it compatible with new implementation
      if (Array.isArray(introIds)) {
        localStorage.removeItem(LocalStorageKey.INTRO_COMPLETED);
      }

      if (!introIds[customerId]?.includes(introId)) {
        this.openImageSwiperModal(steps);
        this.setImageSwiperModalCompleted(introId, customerId);
      }
    });
  }

  private setSwiperModalCompleted(introId: IntroId) {
    this.tagsApiService.postIntroFinished(introId).subscribe();
  }

  private setImageSwiperModalCompleted(introId: IntroId, customerId?: string) {
    if (customerId) {
      this.setIntroCompletedToLocalStorage(introId, customerId);
    } else {
      this.customerId$.subscribe((cid) => {
        this.setIntroCompletedToLocalStorage(introId, cid);
      });
    }
  }

  private getIntroCompletedLocalStorage(): any {
    const introIdsStr = localStorage.getItem(LocalStorageKey.INTRO_COMPLETED);
    let introIdsObj = {};
    if (introIdsStr) {
      introIdsObj = JSON.parse(introIdsStr);
    }
    return introIdsObj;
  }

  private setIntroCompletedToLocalStorage(
    introId: string,
    customerId?: string
  ) {
    const introIdsObj = this.getIntroCompletedLocalStorage();
    if (!Array.isArray(introIdsObj[customerId])) {
      introIdsObj[customerId] = [];
    }
    introIdsObj[customerId].push(introId);
    localStorage.setItem(
      LocalStorageKey.INTRO_COMPLETED,
      JSON.stringify(introIdsObj)
    );
  }
}
